import React, { useState, useEffect, useRef } from 'react'
import { Badge, Button, Pagination, Loader, Center, Text } from '@mantine/core'
import { Table, Row, Col, Form } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { createWeekendWorkRequest } from '../../../api/WeekendWorkAPI'
import {
  faEdit,
  faEye,
  faTrash,
  faPlus,
  faSearch,
  faEraser,
  faDownload,
  faFileExcel
} from '@fortawesome/free-solid-svg-icons'
import { toast } from 'react-toastify'

import {
  AttendanceRecord,
  AttendanceFilters,
  DEFAULT_FILTERS,
  CRUDModalState,
  ATTENDANCE_STATUSES,
  PayrollSummary
} from '../../types/AttendanceTypes'
import {
  getAttendanceRecords,
  getAttendanceForPayroll,
  exportAttendanceRecords,
  updateAttendanceRecord
} from '../../../api/AttendanceAPI'
import AttendanceCRUDModal from './AttendanceCRUDModal'
import { useAuth } from '../../modules/auth'
import { getDate, getPaginationInfoData } from '../../../_metronic/helpers'
import { InputErrorType } from '../../types/OtherTypes'
import InputErrorBox from '../InputErrorBox'
import Select2 from '../select2'

import { createAttendanceEditRequest, getMyAttendanceEditRequests } from '../../../api/AttendanceAPI'
import { Modal, Textarea, TextInput } from '@mantine/core'
import {isValidOptionalAttendanceTime, toAttendanceTimeInputValue} from './attendanceEditValidation'
interface AttendanceTableProps {
  employees: Array<{ value: string; label: string }>
  refreshTrigger?: number
  defaultStatus?: string
}

const AttendanceTable: React.FC<AttendanceTableProps> = ({ employees, refreshTrigger, defaultStatus }) => {
  //Paging Count
  const [totalEmployees, setTotalEmployees] = useState(0)
  const { currentUser } = useAuth()
  const { formattedDate } = getDate()

  // Check if user is RM or HR (has team members to manage)
  //const isRmOrHr = currentUser?.isRmOrHr || employees.length > 1
  const isRmOrHr = !!currentUser?.isRmOrHr

  // Check if user is System Admin (admin@ctrine.com) or Super Admin Role
  const isSystemAdmin = currentUser?.email?.toLowerCase() === 'admin@ctrine.com' ||
    currentUser?.email?.toLowerCase() === 'admin@ctrine.in'
  // Get role from currentUser - check if user has Super Admin role using role.name
  const isSuperAdmin = currentUser?.role?.name === 'Super Admin' ||
    currentUser?.role?.name === 'SUPER_ADMIN'

  // Combined permission: Can add/edit records if RM/HR OR Super Admin OR System Admin
  const canManageRecords = isRmOrHr || isSuperAdmin || isSystemAdmin

  //Comp-off weekend
  // const isWeekend = (dateStr: string) => {
  //   if (!dateStr) return false

  //   const dateOnly = dateStr.split('T')[0]

  //   // Force proper ISO parsing
  //   const date = new Date(dateOnly + 'T00:00:00')

  //   if (isNaN(date.getTime())) return false

  //   const day = date.getDay()

  //   // 0 = Sunday, 6 = Saturday
  //   return day === 0 || day === 6
  // }

    // const isEligibleForCompOff = (record: AttendanceRecord) => {
    //   //  Weekend check
    //   if (!isWeekend(record.date)) return false

    //   //  Must NOT be Absent
    //   if (record.status === 'Absent') return false

    //   //  Duration must exist
    //   if (!record.duration) return false

    //   const timeParts = record.duration.split(':')
    //   if (timeParts.length < 2) return false

    //   const hours = Number(timeParts[0])
    //   const minutes = Number(timeParts[1])

    //   if (isNaN(hours) || isNaN(minutes)) return false

    //   const totalHours = hours + minutes / 60

    //   //  Minimum 6 hours
    //   return totalHours >= 6
    // }
   const isEligibleForCompOff = (record: AttendanceRecord) => {

      if (!record.isWeekOffDay) return false

      if (record.status === 'Absent') return false

      if (!record.duration) return false

      const [hours, minutes] = record.duration.split(':').map(Number)
      const totalHours = hours + minutes / 60

      return totalHours >= 6
    }

    // State management
    const [loading, setLoading] = useState(false)
    const [recordsError, setRecordsError] = useState<string | null>(null)
    const recordsRequestId = useRef(0)
    const countsRequestId = useRef(0)
    const [records, setRecords] = useState<AttendanceRecord[]>([])
    const [pagination, setPagination] = useState({
      currentPage: 1,
      totalPages: 1,
      totalRecords: 0,
      limit: 10
    })
  // Calculate default date range (1 week from today)
  const getDefaultDateRange = () => {
    const today = new Date()
    const oneWeekAgo = new Date(today)
    oneWeekAgo.setDate(today.getDate() - 7)

    return {
      startDate: oneWeekAgo.toISOString().split('T')[0],
      endDate: formattedDate
    }
  }

  const [filters, setFilters] = useState<AttendanceFilters>({
    ...DEFAULT_FILTERS,
    ...getDefaultDateRange(),
    status: defaultStatus || ''
  })
  const [inputError, setInputError] = useState<InputErrorType>({})
  const [modalState, setModalState] = useState<CRUDModalState>({
    isOpen: false,
    mode: 'create'
  })
  const [payrollSummary, setPayrollSummary] = useState<PayrollSummary[]>([])
  const [showPayrollSummary, setShowPayrollSummary] = useState(false)
  const [exportLoading, setExportLoading] = useState(false)

  // Inline edit state
  const [editingCell, setEditingCell] = useState<{ recordId: string; field: 'inTime' | 'outTime' } | null>(null)
  const [editValue, setEditValue] = useState<string>('')
  const [editingLoading, setEditingLoading] = useState(false)

  const [counts, setCounts] = useState({
    present: 0,
    absent: 0,
    late: 0,
    earlyLeave: 0,
    halfDay: 0,
    total: 0
  })
  const [countsAvailable, setCountsAvailable] = useState(false)
  const displayCount = (value: number) => countsAvailable ? value : '—'
  //Paging count will be change as selected status
  const getTotalByStatus = () => {
    if (!countsAvailable) return pagination.totalRecords
    switch (filters.status) {
      case 'Present':
        return counts.present
      case 'Absent':
        return counts.absent
      case 'Late':
        return counts.late
      case 'Early':
        return counts.earlyLeave
      case 'HalfDay':
        return counts.halfDay
      default:
        return counts.total
    }
  }


  // Attendance Edit Request (Employee)
  const [editRequests, setEditRequests] = useState<any[]>([])
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [selectedRecord, setSelectedRecord] = useState<AttendanceRecord | null>(null)
  const [requestedInTime, setRequestedInTime] = useState('')
  const [requestedOutTime, setRequestedOutTime] = useState('')
  const [editTimeErrors, setEditTimeErrors] = useState<{inTime?: string; outTime?: string}>({})
  const [editRequestSubmitting, setEditRequestSubmitting] = useState(false)
  const editRequestSubmitLock = useRef(false)
  const [reason, setReason] = useState('')

  // Comp-Off Request (Employee)
  const [compOffModalOpen, setCompOffModalOpen] = useState(false)
  const [selectedCompOffRecord, setSelectedCompOffRecord] = useState<AttendanceRecord | null>(null)
  const [compOffReason, setCompOffReason] = useState('')
  const [compOffSubmitting, setCompOffSubmitting] = useState(false)
  const compOffSubmitLock = useRef(false)



  // COUNTS (SEPARATE)
  const loadAttendanceCounts = async (activeFilters: AttendanceFilters = filters) => {
    const requestId = ++countsRequestId.current
    try {
      const params = {
        startDate: activeFilters.startDate || undefined,
        endDate: activeFilters.endDate || undefined,
        userId: currentUser?.id,
        targetUserId: isRmOrHr ? (activeFilters.userId || undefined) : currentUser?.id,
        preset: isRmOrHr ? (activeFilters.userId ? 'self' : 'all') : 'self',
        search: activeFilters.search || undefined,
        //  IMPORTANT: DO NOT SEND status
        status: undefined
      }

      const result = await getAttendanceRecords({
        ...params,
        page: 1,
        limit: 1 // we only want counts
      })

      if (requestId === countsRequestId.current && result?.statusCode === 200 && result.data.counts) {
        setCounts(result.data.counts)
        setTotalEmployees(result.data.counts.total)
        setCountsAvailable(true)
      }
    } catch (err) {
      console.error('Failed to load counts', err)
    }
  }

  // Load attendance records
  const loadAttendanceRecords = async (activeFilters: AttendanceFilters = filters) => {
    const requestId = ++recordsRequestId.current
    setRecordsError(null)
    setLoading(true)

    try {
      // Determine preset based on user role and whether a specific user is selected
      let preset = 'self' // Default for regular employees
      let targetUserId: string | undefined = undefined

      // If user is RM/HR, they can see all employees or specific employee
      if (isRmOrHr) {
        preset = activeFilters.userId ? 'self' : 'all'
        targetUserId = activeFilters.userId || undefined
      } else {
        // Non-RM/HR users can only see their own records
        preset = 'self'
        targetUserId = currentUser?.id
      }

      const params = {
        page: activeFilters.page,
        limit: activeFilters.limit,
        startDate: activeFilters.startDate || undefined,
        endDate: activeFilters.endDate || undefined,
        userId: currentUser?.id, // Always send current user's ID
        targetUserId: targetUserId, // Send selected employee's ID as targetUserId (or own ID for non-RM/HR)
        search: activeFilters.search || undefined,
        sortBy: activeFilters.sortBy || undefined,
        sort: activeFilters.sort,
        preset: preset, // Dynamic preset based on user role
        status: activeFilters.status || undefined
      }

      const result = await getAttendanceRecords(params)

      if (requestId !== recordsRequestId.current) return

      if (result?.statusCode === 200 && Array.isArray(result?.data?.attendanceRecords)) {
        setRecordsError(null)
        setRecords(result.data.attendanceRecords || [])

        //  SET COUNTS
        // if (result.data.counts) {
        //   setCounts(result.data.counts)
        // }
        // Ensure pagination has all required fields with defaults
        setPagination({
          currentPage: result.data.pagination?.currentPage || 1,
          totalPages: result.data.pagination?.totalPages || 1,
          totalRecords: result.data.pagination?.totalRecords || 0,
          limit: result.data.pagination?.limit || 10
        })
      } else {
        setRecordsError('Unable to load attendance records. Please check your connection and try again.')
      }
    } catch (error: any) {
      if (requestId === recordsRequestId.current) {
        setRecordsError('Unable to load attendance records. Please check your connection and try again.')
      }
    } finally {
      if (requestId === recordsRequestId.current) setLoading(false)
    }
  }


  const fetchMyEditRequests = async () => {
    try {
      const res = await getMyAttendanceEditRequests()
      setEditRequests(res?.data || [])
    } catch (err) {
      console.error('Failed to fetch edit requests', err)
    }
  }

  const getEditRequestForDate = (date: string) => {
    const normalized = date.split('T')[0]
    return editRequests.find(r => r.attendanceDate === normalized)
  }

  const openEditRequestModal = (record: AttendanceRecord) => {
    setSelectedRecord(record)
    setRequestedInTime(toAttendanceTimeInputValue(record.inTime))
    setRequestedOutTime(toAttendanceTimeInputValue(record.outTime))
    setEditTimeErrors({})
    setReason('')
    setEditModalOpen(true)
  }

  const submitEditRequest = async () => {
    const timeErrors = {
      inTime: isValidOptionalAttendanceTime(requestedInTime)
        ? ''
        : 'Please enter a valid In Time in HH:MM or HH:MM:SS format.',
      outTime: isValidOptionalAttendanceTime(requestedOutTime)
        ? ''
        : 'Please enter a valid Out Time in HH:MM or HH:MM:SS format.',
    }
    setEditTimeErrors(timeErrors)

    if (timeErrors.inTime || timeErrors.outTime) return
    if (!selectedRecord) return
    if (!requestedInTime && !requestedOutTime) {
      setEditTimeErrors({
        inTime: 'Enter at least one attendance time to request an edit.',
        outTime: 'Enter at least one attendance time to request an edit.',
      })
      return
    }
    if (!reason.trim()) {
      toast.error('Reason is required')
      return
    }
    if (editRequestSubmitLock.current) return

    editRequestSubmitLock.current = true
    setEditRequestSubmitting(true)
    try {
      const result = await createAttendanceEditRequest({
        attendanceDate: selectedRecord.date.split('T')[0],
        requestedInTime,
        requestedOutTime,
        reason: reason.trim(),
      })

      if ([200, 201, 202].includes(Number(result?.statusCode))) {
        toast.success('Edit request submitted')
        setEditModalOpen(false)
        fetchMyEditRequests()
      } else {
        const errorMessage = String(result?.message || '')
        toast.error(errorMessage.toLowerCase().includes('already pending')
          ? 'An attendance edit request is already pending for this date. Please wait until the existing request is processed.'
          : result?.statusCode === 400
            ? errorMessage || 'Please review the attendance edit details.'
            : 'Unable to submit the attendance edit request. Please try again.')
      }
    } catch (err) {
      const errorMessage = String((err as any)?.response?.data?.message || (err as any)?.message || '')
      toast.error(errorMessage.toLowerCase().includes('already pending')
        ? 'An attendance edit request is already pending for this date. Please wait until the existing request is processed.'
        : 'Unable to submit the attendance edit request. Please try again.')
    } finally {
      editRequestSubmitLock.current = false
      setEditRequestSubmitting(false)
    }
  }



  const openCompOffModal = (record: AttendanceRecord) => {
    setSelectedCompOffRecord(record)
    setCompOffReason('')
    setCompOffModalOpen(true)
  }

  const submitCompOffRequest = async () => {
    if (!selectedCompOffRecord || !compOffReason.trim()) {
      toast.error('Reason is required')
      return
    }
    if (compOffSubmitLock.current) return

    compOffSubmitLock.current = true
    setCompOffSubmitting(true)
    try {
      const response = await createWeekendWorkRequest({
        userId: currentUser!.id,
        requestDate: selectedCompOffRecord.date.split('T')[0],
        reason: compOffReason.trim(),
        estimatedHours: (() => {
          const hours = Number(selectedCompOffRecord.duration?.split(':')[0]);
          return isNaN(hours) ? 0 : hours;
        })(),
      })

      const statusCode = Number(response?.data?.statusCode ?? response?.status)
      if (![200, 201, 202].includes(statusCode)) {
        toast.error(response?.data?.message || 'Failed to submit Comp-Off request')
        return
      }

      toast.success('Comp-Off request submitted')
      setCompOffModalOpen(false)
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to submit Comp-Off request')
    } finally {
      compOffSubmitLock.current = false
      setCompOffSubmitting(false)
    }
  }

  // Load payroll summary
  const loadPayrollSummary = async () => {
    try {
      const params = {
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
        userId: filters.userId || undefined
      }

      const result = await getAttendanceForPayroll(params)

      if (result.statusCode === 200) {
        setPayrollSummary(result.data || [])
      } else {
        toast.error(result.message || 'Failed to load payroll summary')
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to load payroll summary')
    }
  }

  // Effects
  useEffect(() => {
    loadAttendanceRecords()
    // EMPLOYEE ONLY → load edit requests
    if (!isRmOrHr) {
      fetchMyEditRequests()
    }
  }, [filters.page, filters.limit, refreshTrigger, filters.status])

  // COUNTS (SEPARATE)
  useEffect(() => {
    loadAttendanceCounts()
  }, [refreshTrigger])


  useEffect(() => {
    if (showPayrollSummary) {
      loadPayrollSummary()
    }
  }, [showPayrollSummary, filters.startDate, filters.endDate, filters.userId])

  // Event handlers
  const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target
    setFilters(prev => ({ ...prev, [name]: value }))

    // Clear error
    if (inputError[name]) {
      setInputError(prev => ({ ...prev, [name]: '' }))
    }
  }

  const handleSelectChange = (name: string, value: string | null) => {
    setFilters(prev => ({ ...prev, [name]: value || '' }))
  }

  const handleSearch = () => {
    const activeFilters = {...filters, page: 1}
    if (activeFilters.startDate && activeFilters.endDate && activeFilters.endDate < activeFilters.startDate) {
      setInputError((previous) => ({...previous, endDate: 'End Date must be on or after Start Date.'}))
      return
    }
    loadAttendanceCounts(activeFilters)
    if (filters.page !== 1) {
      setFilters(activeFilters)
      return
    }
    loadAttendanceRecords(activeFilters)
  }

  const handleClearFilters = () => {
    const clearedFilters: AttendanceFilters = {
      ...DEFAULT_FILTERS,
      ...getDefaultDateRange()
    }
    const dataEffectWillLoad =
      filters.page !== clearedFilters.page ||
      filters.limit !== clearedFilters.limit ||
      filters.status !== clearedFilters.status
    setFilters(clearedFilters)
    setInputError({})
    loadAttendanceCounts(clearedFilters)
    if (!dataEffectWillLoad) loadAttendanceRecords(clearedFilters)
  }

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }))
    //setPagination(prev => ({ ...prev, currentPage: page }))
  }

  const startEditingCell = (recordId: string, field: 'inTime' | 'outTime', currentValue: string) => {
    setEditingCell({ recordId, field })
    setEditValue(currentValue)
  }

  const cancelEditing = () => {
    setEditingCell(null)
    setEditValue('')
  }

  // Helper function to calculate duration
  const calculateDuration = (inTime: string, outTime: string): string => {
    if (!inTime || !outTime) return ''

    try {
      const parseTime = (timeStr: string): Date => {
        const today = new Date()
        today.setSeconds(0, 0)
        const cleanTime = timeStr.trim().toUpperCase()

        if (cleanTime.includes('AM') || cleanTime.includes('PM')) {
          const [time, period] = cleanTime.split(/\s+(AM|PM)/)
          const [hours, minutes] = time.split(':').map(Number)
          let hour24 = hours
          if (period === 'PM' && hours !== 12) hour24 += 12
          if (period === 'AM' && hours === 12) hour24 = 0
          today.setHours(hour24, minutes || 0)
        } else {
          const [hours, minutes] = cleanTime.split(':').map(Number)
          today.setHours(hours, minutes || 0)
        }
        return today
      }

      const startTime = parseTime(inTime)
      const endTime = parseTime(outTime)

      // Handle overnight shifts
      if (endTime < startTime) {
        endTime.setDate(endTime.getDate() + 1)
      }

      const diffMs = endTime.getTime() - startTime.getTime()
      const hours = Math.floor(diffMs / (1000 * 60 * 60))
      const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60))

      return `${hours}:${minutes.toString().padStart(2, '0')}`
    } catch (error) {
      console.error('Error calculating duration:', error)
      return ''
    }
  }

  // Helper function to calculate overtime
  const calculateOvertime = (duration: string, standardHours: number = 10): string => {
    if (!duration) return ''

    try {
      const [hours, minutes] = duration.split(':').map(Number)
      const totalHours = hours + (minutes / 60)

      if (totalHours > standardHours) {
        const overtimeHours = totalHours - standardHours
        const otHours = Math.floor(overtimeHours)
        const otMinutes = Math.round((overtimeHours - otHours) * 60)

        return `${otHours}:${otMinutes.toString().padStart(2, '0')}`
      }

      return ''
    } catch (error) {
      console.error('Error calculating overtime:', error)
      return ''
    }
  }

  const saveEditedTime = async (record: AttendanceRecord, field: 'inTime' | 'outTime') => {
    if (!editValue.trim()) {
      toast.error('Time cannot be empty')
      return
    }

    setEditingLoading(true)
    try {
      const oldValue = field === 'inTime' ? record.inTime : record.outTime
      const updateData: any = {
        [field]: editValue
      }

      const result = await updateAttendanceRecord(
        record.employeeCode,
        record.date.split('T')[0],
        updateData
      )

      if (result.statusCode === 200) {
        toast.success(`${field === 'inTime' ? 'In Time' : 'Out Time'} updated successfully`)

        // Use the updated record from backend response
        const updatedRecord = result.data

        // Update the record in state with ALL fields from backend response
        setRecords(prev => prev.map(r =>
          r.id === record.id
            ? {
              ...r,
              inTime: updatedRecord.inTime,      // ← Use backend response
              outTime: updatedRecord.outTime,    // ← Use backend response
              duration: updatedRecord.duration || 'Incomplete',
              overtime: updatedRecord.overtime || 'NA',
              status: updatedRecord.status || r.status
            }
            : r
        ))

        // NOTE: Audit logging is handled by the backend with the correct employee code

        // Reload counts to update the status tabs
        loadAttendanceCounts()

        cancelEditing()
      } else {
        toast.error(result.message || 'Failed to update time')
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to update time')
    } finally {
      setEditingLoading(false)
    }
  }

  const openModal = (mode: CRUDModalState['mode'], record?: AttendanceRecord) => {
    setModalState({
      isOpen: true,
      mode,
      record
    })
  }

  const closeModal = () => {
    setModalState({
      isOpen: false,
      mode: 'create'
    })
  }

  const handleModalSuccess = () => {
    loadAttendanceRecords()
  }

  const handleOptimisticCreated = (record: AttendanceRecord) => {
    // Optimistically add the new record to the top if it matches current filters/date range
    setRecords(prev => [record, ...prev])
    setPagination(prev => ({
      ...prev,
      totalRecords: (prev.totalRecords || 0) + 1
    }))
    // If current page is not 1, switch to 1 so user sees the freshly added row
    setPagination(prev => ({ ...prev, currentPage: 1 }))
    setFilters(prev => ({ ...prev, page: 1 }))
  }

  const getStatusBadge = (status: string) => {
    const statusStyles: Record<string, { bg: string; color: string; label: string }> = {
      'Week-Off': { bg: '#F1F5F9', color: '#64748B', label: 'Week-Off' },
      'Week Off': { bg: '#F1F5F9', color: '#64748B', label: 'Week Off' },
      'Leave': { bg: '#EFF6FF', color: '#2563EB', label: 'Leave' },
      'On Leave': { bg: '#EFF6FF', color: '#2563EB', label: 'On Leave' },
      'Absent': { bg: '#FEF2F2', color: '#DC2626', label: 'Absent' },
      'Present': { bg: '#F0FDF4', color: '#16A34A', label: 'Present' },
      'Late': { bg: '#FFFBEB', color: '#D97706', label: 'Late' },
      'Early': { bg: '#F5F3FF', color: '#7C3AED', label: 'Early Leave' },
      'Early Leave': { bg: '#F5F3FF', color: '#7C3AED', label: 'Early Leave' },
      'HalfDay': { bg: '#FDF2F8', color: '#DB2777', label: 'Half Day' },
      'Half Day': { bg: '#FDF2F8', color: '#DB2777', label: 'Half Day' },
    }

    const style = statusStyles[status] || { bg: '#F1F5F9', color: '#64748B', label: status }
    
    return (
      <span
        style={{
          backgroundColor: style.bg,
          color: style.color,
          padding: '3px 8px',
          borderRadius: '12px',
          fontSize: '10px',
          fontWeight: '600',
          display: 'inline-block',
          whiteSpace: 'nowrap'
        }}
      >
        {style.label}
      </span>
    )
  }

  const formatTime = (time?: string) => {
    if (!time) return 'N/A'

    // If time includes seconds, remove them for display
    if (time.length > 5) {
      return time.substring(0, 5)
    }
    return time
  }

  const handleExport = async () => {
    setExportLoading(true)
    try {
      // Determine preset based on user role and whether a specific user is selected
      let preset = 'self' // Default for regular employees
      let targetUserId: string | undefined = undefined

      // If user is RM/HR, they can see all employees or specific employee
      if (isRmOrHr) {
        preset = filters.userId ? 'self' : 'all' // 'all' when no specific user selected, 'self' when specific user selected
        targetUserId = filters.userId || undefined
      } else {
        // Non-RM/HR users can only see their own records
        preset = 'self'
        targetUserId = currentUser?.id
      }

      const params = {
        page: 1,
        limit: 10000, // Export all records (high limit to get all filtered data)
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
        userId: currentUser?.id, // Always send current user's ID
        targetUserId: targetUserId, // Send selected employee's ID as targetUserId (or own ID for non-RM/HR)
        search: filters.search || undefined,
        sortBy: filters.sortBy || undefined,
        sort: filters.sort,
        preset: preset, // Dynamic preset based on user role
        status: filters.status || undefined // 
      }

      const blob = await exportAttendanceRecords(params)

      // Create download link
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url

      // Generate filename
      const date = new Date().toISOString().split('T')[0]
      link.download = `attendance_records_${date}.xlsx`

      // Trigger download
      document.body.appendChild(link)
      link.click()

      // Cleanup
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      toast.success('Attendance records exported successfully')
    } catch (error: any) {
      console.error('Export error:', error)
      toast.error(error.message || 'Failed to export attendance records')
    } finally {
      setExportLoading(false)
    }
  }


  return (
    <div>
      {/* Filters Section */}
      <div 
        className="card mb-4"
        style={{
          borderRadius: '12px',
          border: '1px solid #E2ECF7',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
          backgroundColor: '#FFFFFF'
        }}
      >
        <div
          className="card-header d-flex justify-content-between align-items-center"
          style={{
            backgroundColor: '#FFFFFF',
            borderBottom: '1px solid #E2ECF7',
            padding: '8px 16px'
          }}
        >
          <h6
            className="card-title mb-0"
            style={{
              color: '#123B73',
              fontWeight: '600',
              fontSize: '12px'
            }}
          >
            Attendance Filters
          </h6>
          <small
            style={{
              color: '#64748B',
              fontSize: '10px'
            }}
          >
            {!filters.startDate && !filters.endDate ?
              'Showing all records' :
              `Showing: ${filters.startDate || 'All'} to ${filters.endDate || 'Today'}`
            }
          </small>
        </div>
        <div className="card-body" style={{ padding: '8px 16px' }}>
          <Row className="align-items-end">
            <Col md={2}>
              <Form.Group className="mb-0">
                <Form.Label
                  className="fw-bold mb-1"
                  style={{ color: '#123B73', fontSize: '10px', fontWeight: '600' }}
                >
                  Start Date
                </Form.Label>
                <Form.Control
                  type="date"
                  name="startDate"
                  value={filters.startDate}
                  onChange={handleFilterChange}
                  style={{
                    borderRadius: '4px',
                    border: '1px solid #E2ECF7',
                    padding: '4px 8px',
                    fontSize: '11px'
                  }}
                />
                <InputErrorBox Message={inputError.startDate} />
              </Form.Group>
            </Col>

            <Col md={2}>
              <Form.Group className="mb-0">
                <Form.Label
                  className="fw-bold mb-1"
                  style={{ color: '#123B73', fontSize: '10px', fontWeight: '600' }}
                >
                  End Date
                </Form.Label>
                <Form.Control
                  type="date"
                  name="endDate"
                  value={filters.endDate}
                  onChange={handleFilterChange}
                  style={{
                    borderRadius: '4px',
                    border: '1px solid #E2ECF7',
                    padding: '4px 8px',
                    fontSize: '11px'
                  }}
                />
                <InputErrorBox Message={inputError.endDate} />
              </Form.Group>
            </Col>


            {/* Employee filter - COMMENTED OUT as requested - Hidden for all users */}
            {/* {(isSuperAdmin || isSystemAdmin) && (
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold">Employee</Form.Label>
                  <Select2
                    data={[{ value: '', label: 'All Employees' }, ...employees]}
                    value={filters.userId || ''}
                    onChange={(value) => handleSelectChange('userId', value)}
                    placeholder="Select employee..."
                  />
                </Form.Group>
              </Col>
            )} */}


            {canManageRecords && <Col md={3}>
              <Form.Group className="mb-0">
                <Form.Label
                  className="fw-bold mb-1"
                  style={{ color: '#123B73', fontSize: '10px', fontWeight: '600' }}
                >
                  Search
                </Form.Label>
                <Form.Control
                  type="text"
                  name="search"
                  value={filters.search}
                  onChange={handleFilterChange}
                  placeholder="Search..."
                  style={{
                    borderRadius: '4px',
                    border: '1px solid #E2ECF7',
                    padding: '4px 8px',
                    fontSize: '11px'
                  }}
                />
              </Form.Group>
            </Col>}

            {canManageRecords && <Col md={5}>
              <div className="d-flex gap-1 align-items-end">
                <button
                  onClick={handleSearch}
                  disabled={loading}
                  style={{
                    backgroundColor: loading ? '#CBD5E1' : '#3B82F6',
                    color: '#FFFFFF',
                    border: 'none',
                    padding: '4px 10px',
                    borderRadius: '4px',
                    fontSize: '11px',
                    fontWeight: '500',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  {loading ? <Loader size="xs" color="white" /> : <FontAwesomeIcon icon={faSearch} style={{ fontSize: '10px' }} />}
                  Search
                </button>

                <button
                  onClick={handleClearFilters}
                  style={{
                    backgroundColor: '#FFFFFF',
                    color: '#64748B',
                    border: '1px solid #E2ECF7',
                    padding: '4px 10px',
                    borderRadius: '4px',
                    fontSize: '11px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  <FontAwesomeIcon icon={faEraser} style={{ fontSize: '10px' }} />
                  Clear
                </button>
                {/* Add Record button for RM/HR, Super Admin, and System Admin */}
                <button
                    onClick={() => openModal('create')}
                    style={{
                      backgroundColor: '#16A34A',
                      color: '#FFFFFF',
                      border: 'none',
                      padding: '4px 10px',
                      borderRadius: '4px',
                      fontSize: '11px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                  >
                    <FontAwesomeIcon icon={faPlus} style={{ fontSize: '10px' }} />
                    Add Record
                </button>

                {/* Temporarily hidden as requested */}
                {/* <Button
                variant="outline"
                color="blue"
                onClick={() => setShowPayrollSummary(!showPayrollSummary)}
              >
                <FontAwesomeIcon icon={faFileExcel} className="me-2" />
                {showPayrollSummary ? 'Hide' : 'Show'} Payroll Summary
              </Button> */}
              </div>
            </Col>}
          </Row>
        </div>
      </div>

      {/* Temporarily hidden as requested */}
      {/* Payroll Summary Section */}
      {/* {showPayrollSummary && (
        <div className="card mb-4">
          <div className="card-header">
            <h5 className="card-title mb-0">Payroll Summary</h5>
          </div>
          <div className="card-body">
            {payrollSummary.length > 0 ? (
              <div className="table-responsive">
                <Table striped bordered hover>
                  <thead>
                    <tr>
                      <th>Employee Code</th>
                      <th>Total Days</th>
                      <th>Present Days</th>
                      <th>Absent Days</th>
                      <th>Late Days</th>
                      <th>Overtime Hours</th>
                      <th>Total Working Hours</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payrollSummary.map((summary, index) => (
                      <tr key={index}>
                        <td className="fw-bold">{summary.employeeCode}</td>
                        <td>{summary.totalDays}</td>
                        <td>
                          <Badge color="green" variant="light">
                            {summary.presentDays}
                          </Badge>
                        </td>
                        <td>
                          <Badge color="red" variant="light">
                            {summary.absentDays}
                          </Badge>
                        </td>
                        <td>
                          <Badge color="orange" variant="light">
                            {summary.lateDays}
                          </Badge>
                        </td>
                        <td>{summary.overtimeHours.toFixed(1)}h</td>
                        <td>{summary.totalWorkingHours.toFixed(1)}h</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            ) : (
              <Text align="center" color="dimmed">
                No payroll data available for the selected period
              </Text>
            )}
          </div>
        </div>
      )} */}

      {/* Main Table */}
      <div 
        className="card"
        style={{
          borderRadius: '12px',
          border: '1px solid #E2ECF7',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
          backgroundColor: '#FFFFFF',
          overflow: 'hidden'
        }}
      >
        <div
          className="card-header d-flex justify-content-between align-items-center"
          style={{
            backgroundColor: '#FFFFFF',
            borderBottom: '1px solid #E2ECF7',
            padding: '12px 16px'
          }}
        >

          {/* LEFT SIDE: Title + Present / Absent buttons */}
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <h5
              className="card-title mb-0"
              style={{
                color: '#123B73',
                fontWeight: '600',
                fontSize: '14px'
              }}
            >
              Attendance Records
              {pagination.totalRecords > 0 && (
                <span
                  className="ms-2"
                  style={{
                    color: '#64748B',
                    fontWeight: '400',
                    fontSize: '12px'
                  }}
                >
                  ({pagination.totalRecords} total)
                </span>
              )}
            </h5>

            {/* Filter Buttons - Modern Segmented Style */}
            <div
              className="d-flex gap-1"
              style={{
                backgroundColor: '#F4F8FD',
                padding: '3px',
                borderRadius: '6px'
              }}
            >
              {/* All Employees */}
              <button
                onClick={() =>
                  setFilters(prev => ({
                    ...prev,
                    status: undefined,
                    page: 1
                  }))
                }
                style={{
                  backgroundColor: !filters.status ? '#3B82F6' : 'transparent',
                  color: !filters.status ? '#FFFFFF' : '#64748B',
                  border: 'none',
                  padding: '6px 10px',
                  borderRadius: '4px',
                  fontSize: '11px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                All
              </button>

              {/* Present Employees */}
              <button
                onClick={() =>
                  setFilters(prev => ({
                    ...prev,
                    status: 'Present',
                    page: 1
                  }))
                }
                style={{
                  backgroundColor: filters.status === 'Present' ? '#3B82F6' : 'transparent',
                  color: filters.status === 'Present' ? '#FFFFFF' : '#64748B',
                  border: 'none',
                  padding: '6px 10px',
                  borderRadius: '4px',
                  fontSize: '11px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                Present ({displayCount(counts.present)})
              </button>

              {/* Absent Employees */}
              <button
                onClick={() =>
                  setFilters(prev => ({
                    ...prev,
                    status: 'Absent',
                    page: 1
                  }))
                }
                style={{
                  backgroundColor: filters.status === 'Absent' ? '#3B82F6' : 'transparent',
                  color: filters.status === 'Absent' ? '#FFFFFF' : '#64748B',
                  border: 'none',
                  padding: '6px 10px',
                  borderRadius: '4px',
                  fontSize: '11px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                Absent ({displayCount(counts.absent)})
              </button>

              {/* Late Employees */}
              <button
                onClick={() =>
                  setFilters(prev => ({
                    ...prev,
                    status: 'Late',
                    page: 1
                  }))
                }
                style={{
                  backgroundColor: filters.status === 'Late' ? '#3B82F6' : 'transparent',
                  color: filters.status === 'Late' ? '#FFFFFF' : '#64748B',
                  border: 'none',
                  padding: '6px 10px',
                  borderRadius: '4px',
                  fontSize: '11px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                Late ({displayCount(counts.late)})
              </button>

              {/* Early Leave Employees */}
              <button
                onClick={() =>
                  setFilters(prev => ({
                    ...prev,
                    status: 'Early',
                    page: 1
                  }))
                }
                style={{
                  backgroundColor: filters.status === 'Early' ? '#3B82F6' : 'transparent',
                  color: filters.status === 'Early' ? '#FFFFFF' : '#64748B',
                  border: 'none',
                  padding: '6px 10px',
                  borderRadius: '4px',
                  fontSize: '11px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                Early Leave ({displayCount(counts.earlyLeave)})
              </button>

              {/* Half Day Employees */}
              <button
                onClick={() =>
                  setFilters(prev => ({
                    ...prev,
                    status: 'HalfDay',
                    page: 1
                  }))
                }
                style={{
                  backgroundColor: filters.status === 'HalfDay' ? '#3B82F6' : 'transparent',
                  color: filters.status === 'HalfDay' ? '#FFFFFF' : '#64748B',
                  border: 'none',
                  padding: '6px 10px',
                  borderRadius: '4px',
                  fontSize: '11px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                Half Day ({displayCount(counts.halfDay)})
              </button>
            </div>
          </div>

          {/* RIGHT SIDE: Export Button */}
          {canManageRecords && (
            <button
              onClick={handleExport}
              disabled={records.length === 0 || exportLoading}
              style={{
                backgroundColor: records.length === 0 || exportLoading ? '#CBD5E1' : '#3B82F6',
                color: '#FFFFFF',
                border: 'none',
                padding: '6px 12px',
                borderRadius: '6px',
                fontSize: '11px',
                fontWeight: '500',
                cursor: records.length === 0 || exportLoading ? 'not-allowed' : 'pointer',
                boxShadow: '0 1px 2px rgba(59, 130, 246, 0.2)',
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              {exportLoading ? (
                <Loader size="xs" color="white" />
              ) : (
                <FontAwesomeIcon icon={faFileExcel} />
              )}
              Export
            </button>
          )}
        </div>

        <div className="card-body" style={{ padding: '0' }}>
          {loading ? (
            <div style={{ padding: '80px 24px', textAlign: 'center' }}>
              <div 
                style={{
                  backgroundColor: '#F4F8FD',
                  width: '60px',
                  height: '60px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px'
                }}
              >
                <i 
                  className="fa fa-spinner fa-spin" 
                  style={{ color: '#3B82F6', fontSize: '24px' }}
                ></i>
              </div>
              <div style={{ color: '#64748B', fontSize: '14px' }}>Loading attendance records...</div>
            </div>
          ) : recordsError ? (
            <div role="alert" className="text-center" style={{padding: '48px 24px'}}>
              <div className="text-danger mb-3">{recordsError}</div>
              <button
                type="button"
                className="btn btn-sm btn-primary"
                onClick={() => loadAttendanceRecords(filters)}
              >
                Retry
              </button>
            </div>
          ) : records.length > 0 ? (
            <>
              <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
                <table 
                  style={{
                    width: '100%',
                    minWidth: '1000px',
                    borderCollapse: 'collapse',
                    backgroundColor: '#FFFFFF'
                  }}
                >
                  <thead>
                    <tr style={{ backgroundColor: '#F4F8FD' }}>
                      <th style={{
                        padding: '12px 16px',
                        textAlign: 'left',
                        color: '#123B73',
                        fontWeight: '600',
                        fontSize: '12px',
                        borderBottom: '1px solid #E2ECF7',
                        whiteSpace: 'nowrap',
                        width: '100px'
                      }}>
                        Employee Code
                      </th>
                      <th style={{
                        padding: '12px 16px',
                        textAlign: 'left',
                        color: '#123B73',
                        fontWeight: '600',
                        fontSize: '12px',
                        borderBottom: '1px solid #E2ECF7',
                        whiteSpace: 'nowrap',
                        width: '150px'
                      }}>
                        Employee Name
                      </th>
                      <th style={{
                        padding: '12px 16px',
                        textAlign: 'left',
                        color: '#123B73',
                        fontWeight: '600',
                        fontSize: '12px',
                        borderBottom: '1px solid #E2ECF7',
                        whiteSpace: 'nowrap',
                        width: '100px'
                      }}>
                        Date
                      </th>
                      <th style={{
                        padding: '12px 16px',
                        textAlign: 'left',
                        color: '#123B73',
                        fontWeight: '600',
                        fontSize: '12px',
                        borderBottom: '1px solid #E2ECF7',
                        whiteSpace: 'nowrap',
                        width: '120px'
                      }}>
                        Shift
                      </th>
                      <th style={{
                        padding: '12px 16px',
                        textAlign: 'left',
                        color: '#123B73',
                        fontWeight: '600',
                        fontSize: '12px',
                        borderBottom: '1px solid #E2ECF7',
                        whiteSpace: 'nowrap',
                        width: '100px'
                      }}>
                        Status
                      </th>
                      <th style={{
                        padding: '12px 16px',
                        textAlign: 'left',
                        color: '#123B73',
                        fontWeight: '600',
                        fontSize: '12px',
                        borderBottom: '1px solid #E2ECF7',
                        whiteSpace: 'nowrap',
                        width: '110px'
                      }}>
                        In Time
                      </th>
                      <th style={{
                        padding: '12px 16px',
                        textAlign: 'left',
                        color: '#123B73',
                        fontWeight: '600',
                        fontSize: '12px',
                        borderBottom: '1px solid #E2ECF7',
                        whiteSpace: 'nowrap',
                        width: '110px'
                      }}>
                        Out Time
                      </th>
                      <th style={{
                        padding: '12px 16px',
                        textAlign: 'left',
                        color: '#123B73',
                        fontWeight: '600',
                        fontSize: '12px',
                        borderBottom: '1px solid #E2ECF7',
                        whiteSpace: 'nowrap',
                        width: '90px'
                      }}>
                        Duration
                      </th>
                      <th style={{
                        padding: '12px 16px',
                        textAlign: 'left',
                        color: '#123B73',
                        fontWeight: '600',
                        fontSize: '12px',
                        borderBottom: '1px solid #E2ECF7',
                        whiteSpace: 'nowrap',
                        width: '90px'
                      }}>
                        Overtime
                      </th>
                      <th style={{
                        padding: '12px 16px',
                        textAlign: 'left',
                        color: '#123B73',
                        fontWeight: '600',
                        fontSize: '12px',
                        borderBottom: '1px solid #E2ECF7',
                        whiteSpace: 'nowrap',
                        width: 'auto'
                      }}>
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {records.map((record) => {
                      //Own employee can request edit for their record (if no existing pending request for that date) 
                      const isOwnRecord = record.user?.id === currentUser?.id;
                      return (
                      <tr 
                        key={record.id}
                        style={{
                          borderBottom: '1px solid #E2ECF7',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F4F8FD'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                      >
                        <td style={{
                          padding: '8px 12px',
                          color: '#123B73',
                          fontWeight: '600',
                          fontSize: '12px',
                          verticalAlign: 'middle'
                        }}>
                          {record.employeeCode}
                        </td>
                        <td style={{
                          padding: '8px 12px',
                          color: '#123B73',
                          fontWeight: '400',
                          fontSize: '12px',
                          verticalAlign: 'middle'
                        }}>
                          {record.user
                            ? `${record.user.firstName || ''} ${record.user.lastName || ''}`.trim()
                            : 'N/A'}
                        </td>
                        <td style={{
                          padding: '8px 12px',
                          color: '#64748B',
                          fontWeight: '400',
                          fontSize: '12px',
                          verticalAlign: 'middle'
                        }}>
                          {new Date(record.date).toLocaleDateString()}
                        </td>
                        <td style={{
                          padding: '8px 12px',
                          color: '#64748B',
                          fontWeight: '400',
                          fontSize: '12px',
                          verticalAlign: 'middle',
                          whiteSpace: 'nowrap'
                        }}>
                          {record.shift}
                        </td>
                        <td style={{
                          padding: '8px 12px',
                          verticalAlign: 'middle'
                        }}>
                          {getStatusBadge(record.status)}
                        </td>
                        <td style={{
                          padding: '8px 12px',
                          verticalAlign: 'middle'
                        }}>
                          {editingCell?.recordId === record.id && editingCell?.field === 'inTime' ? (
                            <div className="d-flex gap-1 align-items-center">
                              <Form.Control
                                type="time"
                                value={editValue}
                                onChange={(e) => setEditValue(e.target.value)}
                                autoFocus
                                style={{
                                  padding: '4px 6px',
                                  fontSize: '11px',
                                  borderRadius: '4px',
                                  border: '1px solid #E2ECF7',
                                  width: '70px'
                                }}
                              />
                              <button
                                onClick={() => saveEditedTime(record, 'inTime')}
                                disabled={editingLoading}
                                style={{
                                  backgroundColor: '#16A34A',
                                  color: '#FFFFFF',
                                  border: 'none',
                                  padding: '4px 6px',
                                  borderRadius: '4px',
                                  cursor: editingLoading ? 'not-allowed' : 'pointer',
                                  fontSize: '10px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  minWidth: '18px'
                                }}
                              >
                                {editingLoading ? '...' : '✓'}
                              </button>
                              <button
                                onClick={cancelEditing}
                                disabled={editingLoading}
                                style={{
                                  backgroundColor: '#EF4444',
                                  color: '#FFFFFF',
                                  border: 'none',
                                  padding: '4px 6px',
                                  borderRadius: '4px',
                                  cursor: editingLoading ? 'not-allowed' : 'pointer',
                                  fontSize: '10px'
                                }}
                              >
                                ✕
                              </button>
                            </div>
                          ) : (
                            <div className="d-flex gap-1 align-items-center">
                              <span style={{ color: '#64748B', fontSize: '12px' }}>
                                {formatTime(record.inTime)}
                              </span>
                              {canManageRecords && (
                                <button
                                  onClick={() => startEditingCell(record.id, 'inTime', record.inTime || '')}
                                  title="Edit In Time"
                                  style={{
                                    backgroundColor: '#EAF4FF',
                                    color: '#2563EB',
                                    border: 'none',
                                    padding: '4px 6px',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '10px',
                                    transition: 'all 0.2s'
                                  }}
                                >
                                  <FontAwesomeIcon icon={faEdit} />
                                </button>
                              )}
                            </div>
                          )}
                        </td>
                        <td style={{
                          padding: '8px 12px',
                          verticalAlign: 'middle'
                        }}>
                          {editingCell?.recordId === record.id && editingCell?.field === 'outTime' ? (
                            <div className="d-flex gap-1 align-items-center">
                              <Form.Control
                                type="time"
                                value={editValue}
                                onChange={(e) => setEditValue(e.target.value)}
                                autoFocus
                                style={{
                                  padding: '4px 6px',
                                  fontSize: '11px',
                                  borderRadius: '4px',
                                  border: '1px solid #E2ECF7',
                                  width: '70px'
                                }}
                              />
                              <button
                                onClick={() => saveEditedTime(record, 'outTime')}
                                disabled={editingLoading}
                                style={{
                                  backgroundColor: '#16A34A',
                                  color: '#FFFFFF',
                                  border: 'none',
                                  padding: '4px 6px',
                                  borderRadius: '4px',
                                  cursor: editingLoading ? 'not-allowed' : 'pointer',
                                  fontSize: '10px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  minWidth: '18px'
                                }}
                              >
                                {editingLoading ? '...' : '✓'}
                              </button>
                              <button
                                onClick={cancelEditing}
                                disabled={editingLoading}
                                style={{
                                  backgroundColor: '#EF4444',
                                  color: '#FFFFFF',
                                  border: 'none',
                                  padding: '4px 6px',
                                  borderRadius: '4px',
                                  cursor: editingLoading ? 'not-allowed' : 'pointer',
                                  fontSize: '10px'
                                }}
                              >
                                ✕
                              </button>
                            </div>
                          ) : (
                            <div className="d-flex gap-1 align-items-center">
                              <span style={{ color: '#64748B', fontSize: '12px' }}>
                                {formatTime(record.outTime)}
                              </span>
                              {canManageRecords && (
                                <button
                                  onClick={() => startEditingCell(record.id, 'outTime', record.outTime || '')}
                                  title="Edit Out Time"
                                  style={{
                                    backgroundColor: '#EAF4FF',
                                    color: '#2563EB',
                                    border: 'none',
                                    padding: '4px 6px',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '10px',
                                    transition: 'all 0.2s'
                                  }}
                                >
                                  <FontAwesomeIcon icon={faEdit} />
                                </button>
                              )}
                            </div>
                          )}
                        </td>
                        <td style={{
                          padding: '8px 12px',
                          color: '#64748B',
                          fontWeight: '400',
                          fontSize: '12px',
                          verticalAlign: 'middle'
                        }}>
                          {record.inTime && record.outTime
                            ? calculateDuration(record.inTime, record.outTime)
                            : 'N/A'}
                        </td>
                        <td style={{
                          padding: '8px 12px',
                          color: '#64748B',
                          fontWeight: '400',
                          fontSize: '12px',
                          verticalAlign: 'middle'
                        }}>
                          {record.overtime || 'N/A'}
                        </td>

                        {/* ###### */}
                        <td style={{
                          padding: '8px 12px',
                          verticalAlign: 'middle'
                        }}>
                          <div className="d-flex gap-1 align-items-center">
                            {/* VIEW → ALL USERS */}
                            <button
                              onClick={() => openModal('view', record)}
                              title="View Record"
                              style={{
                                backgroundColor: '#EAF4FF',
                                color: '#2563EB',
                                border: 'none',
                                padding: '6px 8px',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '10px',
                                transition: 'all 0.2s',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            >
                              <FontAwesomeIcon icon={faEye} />
                            </button>

                            {/*  RM / HR / Super Admin / System Admin → DELETE */}
                            {canManageRecords && (
                              <button
                                onClick={() => openModal('delete', record)}
                                title="Delete Record"
                                style={{
                                  backgroundColor: '#FEF2F2',
                                  color: '#EF4444',
                                  border: 'none',
                                  padding: '6px 8px',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '10px',
                                  transition: 'all 0.2s',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                              >
                                <FontAwesomeIcon icon={faTrash} />
                              </button>
                            )}
                            {/*  EMPLOYEE → REQUEST EDIT (only for non-managers) */}
                            {/* {!canManageRecords && (() => { */}
                            {isOwnRecord && (() => {
                              const editReq = getEditRequestForDate(record.date.split('T')[0])
                              if (!editReq) {
                                return (
                                  <button
                                    onClick={() => openEditRequestModal(record)}
                                    style={{
                                      backgroundColor: '#EAF4FF',
                                      color: '#2563EB',
                                      border: 'none',
                                      padding: '6px 8px',
                                      borderRadius: '4px',
                                      cursor: 'pointer',
                                      fontSize: '10px',
                                      fontWeight: '500',
                                      transition: 'all 0.2s',
                                      whiteSpace: 'nowrap'
                                    }}
                                  >
                                    Request Edit
                                  </button>
                                )
                              }
                              return (
                                <span
                                  style={{
                                    backgroundColor:
                                      editReq.status === 'PENDING' ? '#FFFBEB' :
                                      editReq.status === 'APPROVED' ? '#F0FDF4' : '#FEF2F2',
                                    color:
                                      editReq.status === 'PENDING' ? '#D97706' :
                                      editReq.status === 'APPROVED' ? '#16A34A' : '#DC2626',
                                    padding: '6px 12px',
                                    borderRadius: '20px',
                                    fontSize: '11px',
                                    fontWeight: '600',
                                    display: 'inline-block'
                                  }}
                                >
                                  {editReq.status}
                                </span>
                              )
                            })()}

                            {/*  EMPLOYEE → COMP-OFF (All Days ONLY) */}
                            {/* {!canManageRecords && (

                              <Button
                                size="xs"
                                color="green"
                                variant="light"
                                onClick={() => openCompOffModal(record)}
                              >
                                Comp-Off
                              </Button>
                            )} */}

                             {/*  EMPLOYEE → COMP-OFF (Weekend Present & Absent Days ONLY) */}
                            {/* {!canManageRecords && isWeekend(record.date) && (
                              <Button
                                size="xs"
                                color="green"
                                variant="light"
                                onClick={() => openCompOffModal(record)}
                              >
                                Comp-Off
                              </Button>
                            )} */}
                            {/* {!canManageRecords && isEligibleForCompOff(record) && ( */}
                            {isOwnRecord && isEligibleForCompOff(record) && (
                              <button
                                onClick={() => openCompOffModal(record)}
                                style={{
                                  backgroundColor: '#F0FDF4',
                                  color: '#16A34A',
                                  border: 'none',
                                  padding: '8px 12px',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  fontSize: '12px',
                                  fontWeight: '500',
                                  transition: 'all 0.2s'
                                }}
                              >
                                Comp-Off
                              </button>
                            )}

                          </div>
                        </td>
                        {/* #### */}
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
                <Modal
                  opened={editModalOpen}
                  onClose={() => setEditModalOpen(false)}
                  withCloseButton
                  size="md"
                  centered
                  styles={{
                    title: {
                      color: '#123B73',
                      fontWeight: 600,
                      fontSize: '18px'
                    },
                    header: {
                      borderBottom: '1px solid #E2ECF7',
                      padding: '16px 24px'
                    },
                    body: {
                      padding: '24px'
                    },
                    content: {
                      borderRadius: '16px',
                      border: '1px solid #E2ECF7',
                      boxShadow: '0 8px 32px rgba(59, 130, 246, 0.12)'
                    }
                  }}
                  title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '10px',
                        background: 'linear-gradient(135deg, #3B82F6 0%, #60A5FA 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 4px 12px rgba(59, 130, 246, 0.25)'
                      }}>
                        <svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="pen-to-square" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" style={{ fontSize: '16px', width: '16px', height: '16px', color: '#FFFFFF' }}>
                          <path fill="currentColor" d="M362.7 19.3L314.3 67.7 444.3 197.7l48.4-48.4c25-25 25-65.5 0-90.5L453.3 19.3c-25-25-65.5-25-90.5 0zm-71 71L58.6 323.5c-10.4 10.4-18 23.3-22.2 37.4L1 481.2C-1.5 489.7 .8 498.8 7 505s15.3 8.5 23.7 6.1l120.3-35.4c14.1-4.2 27-11.8 37.4-22.2L421.7 220.3 291.7 90.3z"></path>
                        </svg>
                      </div>
                      <span>Request Attendance Edit</span>
                    </div>
                  }
                >
                  <TextInput
                    label="In Time"
                    type="time"
                    step={1}
                    placeholder="HH:MM"
                    description="Use 24-hour time (HH:MM or HH:MM:SS)."
                    error={editTimeErrors.inTime}
                    value={requestedInTime}
                    onChange={(e) => {
                      setRequestedInTime(e.target.value)
                      setEditTimeErrors((previous) => ({...previous, inTime: ''}))
                    }}
                    styles={{
                      label: {
                        color: '#123B73',
                        fontSize: '13px',
                        fontWeight: 600,
                        marginBottom: '8px'
                      },
                      input: {
                        border: '1px solid #E2ECF7',
                        borderRadius: '12px',
                        fontSize: '14px',
                        color: '#123B73',
                        padding: '12px 16px'
                      }
                    }}
                  />

                  <TextInput
                    label="Out Time"
                    type="time"
                    step={1}
                    placeholder="HH:MM"
                    description="Use 24-hour time (HH:MM or HH:MM:SS)."
                    error={editTimeErrors.outTime}
                    value={requestedOutTime}
                    onChange={(e) => {
                      setRequestedOutTime(e.target.value)
                      setEditTimeErrors((previous) => ({...previous, outTime: ''}))
                    }}
                    mt="md"
                    styles={{
                      label: {
                        color: '#123B73',
                        fontSize: '13px',
                        fontWeight: 600,
                        marginBottom: '8px'
                      },
                      input: {
                        border: '1px solid #E2ECF7',
                        borderRadius: '12px',
                        fontSize: '14px',
                        color: '#123B73',
                        padding: '12px 16px'
                      }
                    }}
                  />

                  <Textarea
                    label="Reason"
                    required
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    mt="md"
                    rows={4}
                    styles={{
                      label: {
                        color: '#123B73',
                        fontSize: '13px',
                        fontWeight: 600,
                        marginBottom: '8px'
                      },
                      input: {
                        border: '1px solid #E2ECF7',
                        borderRadius: '12px',
                        fontSize: '14px',
                        color: '#123B73',
                        padding: '12px 16px'
                      }
                    }}
                  />

                  <Button
                    fullWidth
                    onClick={submitEditRequest}
                    disabled={editRequestSubmitting}
                    variant="filled"
                    radius="md"
                    mt="md"
                    size="lg"
                    styles={{
                      root: {
                        background: 'linear-gradient(135deg, #3B82F6 0%, #60A5FA 100%) !important',
                        boxShadow: '0 4px 14px rgba(59, 130, 246, 0.35)',
                        padding: '16px 32px',
                        fontSize: '15px',
                        fontWeight: 500,
                        borderRadius: '12px',
                        color: '#FFFFFF !important',
                        border: 'none',
                        height: '50px',
                        minHeight: '50px'
                      },
                      label: {
                        color: '#FFFFFF !important',
                        fontSize: '15px',
                        fontWeight: 600
                    }
                    }}
                  >
                    Submit for Approval
                  </Button>
                </Modal>

                <Modal
                  opened={compOffModalOpen}
                  onClose={() => setCompOffModalOpen(false)}
                  title="Request Comp-Off"
                >
                  <div style={{ marginBottom: '16px', color: '#64748B', fontSize: '14px' }}>
                    Date: {selectedCompOffRecord?.date &&
                      new Date(selectedCompOffRecord.date).toLocaleDateString()}
                  </div>

                  <Textarea
                    label="Reason"
                    required
                    value={compOffReason}
                    onChange={(e) => setCompOffReason(e.target.value)}
                  />

                  <Button
                    fullWidth
                    onClick={submitCompOffRequest}
                    disabled={compOffSubmitting}
                    variant="filled"
                    color="blue"
                    radius="md"
                    mt="md"
                  >
                    {compOffSubmitting ? 'Submitting...' : 'Submit Comp-Off Request'}
                  </Button>
                </Modal>

              </div>
              {/* Pagination + Employee Count */}
              {records.length > 0 && (
                <div
                  className="d-flex justify-content-between align-items-center"
                  style={{
                    padding: '10px 16px',
                    borderTop: '1px solid #E2ECF7',
                    backgroundColor: '#FFFFFF'
                  }}
                >

                  {/*  Employee count */}
                  <span
                    style={{ color: '#64748B', fontSize: '11px' }}
                  >
                    {(() => {
                      const total = getTotalByStatus()

                      if (total === 0) {
                        return '0 - 0 of 0'
                      }

                      // Cap the page at totalPages to prevent invalid ranges
                      const maxPage = Math.ceil(total / pagination.limit)
                      const currentPage = Math.min(filters.page, maxPage)

                      const start = (currentPage - 1) * pagination.limit + 1
                      const end = Math.min(currentPage * pagination.limit, total)

                      return `${start} - ${end} of ${total}`
                    })()}
                  </span>

                  {/*  Pagination numbers */}
                  {pagination.totalPages > 1 && (
                    <div className="d-flex gap-1">
                      <button
                        onClick={() => handlePageChange(filters.page - 1)}
                        disabled={filters.page === 1}
                        style={{
                          backgroundColor: filters.page === 1 ? '#F1F5F9' : '#FFFFFF',
                          color: filters.page === 1 ? '#CBD5E1' : '#64748B',
                          border: '1px solid #E2ECF7',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          cursor: filters.page === 1 ? 'not-allowed' : 'pointer',
                          fontSize: '10px',
                          transition: 'all 0.2s'
                        }}
                      >
                        Prev
                      </button>

                      {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                        let pageNum;
                        if (pagination.totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (filters.page <= 3) {
                          pageNum = i + 1;
                        } else if (filters.page >= pagination.totalPages - 2) {
                          pageNum = pagination.totalPages - 4 + i;
                        } else {
                          pageNum = filters.page - 2 + i;
                        }

                        return (
                          <button
                            key={pageNum}
                            onClick={() => handlePageChange(pageNum)}
                            style={{
                              backgroundColor: filters.page === pageNum ? '#3B82F6' : '#FFFFFF',
                              color: filters.page === pageNum ? '#FFFFFF' : '#64748B',
                              border: '1px solid #E2ECF7',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '10px',
                              transition: 'all 0.2s',
                              minWidth: '24px'
                            }}
                          >
                            {pageNum}
                          </button>
                        );
                      })}

                      <button
                        onClick={() => handlePageChange(filters.page + 1)}
                        disabled={filters.page === pagination.totalPages}
                        style={{
                          backgroundColor: filters.page === pagination.totalPages ? '#F1F5F9' : '#FFFFFF',
                          color: filters.page === pagination.totalPages ? '#CBD5E1' : '#64748B',
                          border: '1px solid #E2ECF7',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          cursor: filters.page === pagination.totalPages ? 'not-allowed' : 'pointer',
                          fontSize: '10px',
                          transition: 'all 0.2s'
                        }}
                      >
                        Next
                      </button>
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <div 
              style={{
                padding: '80px 24px',
                textAlign: 'center',
                backgroundColor: '#FFFFFF'
              }}
            >
              <div 
                style={{
                  backgroundColor: '#F4F8FD',
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 20px'
                }}
              >
                <i 
                  className="fa fa-calendar-xmark fa-2x" 
                  style={{ color: '#64748B', fontSize: '32px' }}
                ></i>
              </div>
              <div 
                style={{ 
                  color: '#123B73', 
                  fontWeight: '600', 
                  marginBottom: '8px',
                  fontSize: '18px'
                }}
              >
                No attendance records found
              </div>
              <div 
                style={{ 
                  color: '#64748B',
                  fontSize: '14px'
                }}
              >
                Try adjusting your filters or add a new record
              </div>
            </div>
          )}
        </div>
      </div>

      {/* CRUD Modal */}
      <AttendanceCRUDModal
        modalState={modalState}
        onClose={closeModal}
        onSuccess={handleModalSuccess}
        employees={employees}
        onCreated={handleOptimisticCreated}
      />
    </div>
  )
}

export default AttendanceTable
